import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlPlateType, UserControlPlateDTO } from '../../models/models.swagger.generated';
import { GridBaseComponent } from '../../shared/grid-base/grid-base.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ClientService } from '../../services/client/client.service';
import { Validators, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { CommandModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { Observable, of } from 'rxjs';
import { IAutocompleteItem } from '../../shared/autocomplete/autocomplete-item';
import { CountryISO2 } from '../../shared/countryISO2-enum';
import { AuthService } from '../../services/auth.service';
import { SfCommandButtonType, SfRequestTypes } from '../../models/syncfusion-data';
import { EnumDropDownListComponent } from '../../shared/enum-drop-down-list/enum-drop-down-list.component';
import { AppErrorMessagesComponent } from '../../error-messages/app-error-messages.component';
import { AutocompleteComponent } from '../../shared/autocomplete/autocomplete.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { UserControlPlateFormType } from './deposited-number-plates-form.type';
import { CustomValidatorsFactory } from '../../shared/validators/custom-validators-factory';
import { ControlPlateUtils } from '../../shared/utils/control-plate-utils';

@Component({
    selector: 'app-deposited-number-plates',
    templateUrl: './deposited-number-plates.component.html',
    styleUrls: ['./deposited-number-plates.component.scss'],
    imports: [
        GridModule,
        ToolbarModule,
        ReactiveFormsModule,
        AutocompleteComponent,
        AppErrorMessagesComponent,
        EnumDropDownListComponent,
        TranslateModule,
    ]
})
export class DepositedNumberPlatesComponent extends GridBaseComponent implements OnInit {

  @Input() controlPlates: UserControlPlateDTO[] = [];
  @Output() controlPlatesChange = new EventEmitter<UserControlPlateDTO[]>();

  public get isUserStandardUser() {
    return this.authService.isUserStandardUser();
  }

  public get selectedCountry(): string {
    return this.gridEditForm?.controls.countryCode.value;
  }

  public set selectedCountry(value: string) {
    this.gridEditForm?.controls.countryCode.setValue(value);
    this.gridEditForm.markAsDirty();
  }

  public countries: IAutocompleteItem[] = [];
  public ControlPlateType = ControlPlateType;

  constructor(translateService: TranslateService,
              clientService: ClientService,
              private fb: FormBuilder,
              private translate: TranslateService,
              private authService: AuthService) {
    super(clientService, translateService, null);
    this.gridCommands = [];
  }

  ngOnInit(): void {
    this.editDialogTitle = 'i18n.base.user-management.deposited-number-plates.edit-dialog-title';
    this.addDialogTitle = 'i18n.base.user-management.deposited-number-plates.add-dialog-title';
    if (this.isUserStandardUser) {
      this.gridCommands = [
        {
          type: SfCommandButtonType.Edit,
          buttonOption: {
            cssClass: 'icon-btn',
            iconCss: 'icon-edit e-icons',
          },
        },
        {
          type: SfCommandButtonType.Delete,
          buttonOption: {
            cssClass: 'icon-btn',
            iconCss: 'icon-trash-can e-icons e-danger',
          },
        },
      ] as CommandModel[];
    }

    // eslint-disable-next-line guard-for-in
    for (const country in CountryISO2) {
      this.countries.push({id: country, name: this.translate.instant('i18n.shared.CountryISO2.' + country)});
    }
  }

  public getCountries(searchText: string): Observable<IAutocompleteItem[]> {
    return of(this.countries.filter(country => country.name.toLowerCase().includes(searchText?.toLowerCase())));
  }

  public override actionBegin(args) {
    if ((args.requestType === SfRequestTypes.BeginEdit || args.requestType === SfRequestTypes.Add) && !this.isUserStandardUser) {
      args.cancel = true;
      return;
    }
    super.actionBegin(args);
  }

  public override actionComplete(args) {
    super.actionComplete(args);
    this.controlPlatesChange.emit(this.controlPlates);
  }

  public formatControlPlateType(data): string {
    return 'i18n.shared.controlPlateType.' + ControlPlateType[data.controlPlateType];
  }

  protected getEditDialogTitleParams(rowData: any): any {
  }

  protected initializeEditForm(data: UserControlPlateDTO): void {
    this.gridEditForm = this.fb.group<UserControlPlateFormType>({
      id: this.fb.control(data.id ?? 0),
      countryCode: this.fb.control(data.countryCode, [Validators.required]),
      controlPlateNumber: this.fb.control(data.originalControlPlate || data.controlPlateNumber, [Validators.required, Validators.maxLength(10), CustomValidatorsFactory.controlPlateValidatorFn()]),
      controlPlateType: this.fb.control(data.controlPlateType, [Validators.required])
    });

    ControlPlateUtils.setupControlPlateNormalization(this.gridEditForm.controls.controlPlateNumber);
  }
}
